import { FC, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "common/components";
import { Settings, MoreHoriz, Info } from "@mui/icons-material";
import HeaderNew from "common/components/custom/header-new/HeaderNew";
import {
  useHybridSearchQuery,
  useHybridKgQuery
} from "../../data/api/queries/search";
import { SearchResultsPayload } from "../../data/models/search-hub.model";
import {
  GraphNode,
  GraphEdge,
  NodeType,
  NodeProperties,
  RelationshipType
} from "modules/knowledge-library/data/models/knowledge-graph.model";
import GraphCanvas from "modules/knowledge-library/pages/knowledge-library/components/graph-canvas/GraphCanvas";
import PropertiesPanel from "modules/knowledge-library/pages/knowledge-library/components/PropertiesPanel";
import SearchShimmer from "../component/search-shimmer/SearchShimmer";
import "./SearchDetails.scss";

const SearchDetails: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchState = location.state as {
    results?: SearchResultsPayload;
    query?: string;
  } | null;

  const searchQuery = searchState?.query || "";
  const hasPassedResults = !!searchState?.results;

  const {
    data: apiData,
    isLoading,
    error: apiError
  } = useHybridSearchQuery(
    { nl_query: searchQuery },
    { skip: !searchQuery || hasPassedResults }
  );

  const data = hasPassedResults ? searchState?.results : apiData;
  const loading = !hasPassedResults && isLoading;
  const error = apiError ? "Search failed. Please try again." : null;

  // Hybrid KG query — triggered alongside hybrid-search
  const { data: kgData, isLoading: kgLoading } = useHybridKgQuery(
    { nl_query: searchQuery },
    { skip: !searchQuery }
  );

  const [activeTab, setActiveTab] = useState<string>("overview");
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const kgSectionRef = useRef<HTMLDivElement>(null);

  const sr = data?.structured_response;
  const kgAnswer = kgData?.structured_response?.answer || kgData?.answer || "";

  // Handle all response shapes: top-level, structured_response, or data wrapper
  const retrievedNodes =
    kgData?.structured_response?.retrieved_nodes ||
    kgData?.retrieved_nodes ||
    kgData?.data?.retrieved_nodes;

  // Extract context from retrieved nodes for dynamic display
  const assetNode = retrievedNodes?.find(
    (n: any) => n.label?.toLowerCase() === "asset"
  );
  const equipmentNode = retrievedNodes?.find(
    (n: any) => n.label?.toLowerCase() === "equipment"
  );
  const systemNode = retrievedNodes?.find(
    (n: any) => n.label?.toLowerCase() === "system"
  );

  // Transform retrieved_nodes into graph data (same logic as knowledge-library ViewData)
  const { nodes, edges, properties, connectedEntities, nodePropertiesMap } =
    useMemo(() => {
      if (
        !retrievedNodes ||
        !Array.isArray(retrievedNodes) ||
        retrievedNodes.length === 0
      ) {
        return {
          nodes: [] as GraphNode[],
          edges: [] as GraphEdge[],
          properties: null as NodeProperties | null,
          connectedEntities: [] as any[],
          nodePropertiesMap: {} as Record<string, NodeProperties>
        };
      }

      const labelToNodeType = (label: string): NodeType => {
        const l = label?.toLowerCase() || "";
        if (l === "asset") return NodeType.ASSET;
        if (l === "system") return NodeType.SYSTEM;
        if (l === "subsystem") return NodeType.SUBSYSTEM;
        if (l === "equipment") return NodeType.EQUIPMENT;
        if (l === "requirement") return NodeType.REQUIREMENT;
        if (l === "discipline") return NodeType.DISCIPLINE;
        if (l === "document") return NodeType.DOCUMENT;
        if (l === "component") return NodeType.COMPONENT;
        if (l === "sensor") return NodeType.SENSOR;
        return NodeType.SYSTEM;
      };

      const mapRelationship = (rel: unknown): RelationshipType => {
        const r = (typeof rel === "string" ? rel : "").toUpperCase();
        if (r === "CONTAINS") return RelationshipType.CONTAINS;
        if (r === "OWNED_BY") return RelationshipType.OWNED_BY;
        if (r === "HAS") return RelationshipType.HAS;
        if (r === "CONSTRAINT") return RelationshipType.CONSTRAINT;
        if (r.includes("COMPONENT")) return RelationshipType.HAS_COMPONENT;
        if (r.includes("REQUIREMENT")) return RelationshipType.HAS_REQUIREMENT;
        if (r.includes("SOURCED") || r.includes("DOCUMENT"))
          return RelationshipType.SOURCED_FROM;
        if (r.includes("MONITOR") || r.includes("SENSOR"))
          return RelationshipType.MONITORED_BY;
        return RelationshipType.CONTAINS;
      };

      const getDisplayName = (item: any): string => {
        if (item.name && item.name.trim()) return item.name;
        if (item.properties?.type) return item.properties.type;
        if (item.properties?.name) return item.properties.name;
        return item.label;
      };

      // Deduplicate nodes
      const uniqueMap = new Map<string, any>();
      retrievedNodes.forEach((item: any) => {
        const displayName = getDisplayName(item);
        const key = `${item.label}-${displayName}`;
        if (!uniqueMap.has(key)) {
          uniqueMap.set(key, { ...item, _displayName: displayName });
        }
      });
      const uniqueItems = Array.from(uniqueMap.values());

      const COL_GAP = 350;
      const ROW_GAP = 250;
      const START_X = 300;
      const START_Y = 150;

      const typeHierarchy = (type: NodeType): number => {
        switch (type) {
          case NodeType.ASSET:
            return 0;
          case NodeType.SYSTEM:
            return 1;
          case NodeType.SUBSYSTEM:
            return 2;
          case NodeType.EQUIPMENT:
            return 3;
          case NodeType.COMPONENT:
            return 4;
          default:
            return 5;
        }
      };

      const nameToIndex = new Map<string, number>();
      uniqueItems.forEach((item: any, i: number) => {
        nameToIndex.set(item._displayName, i);
        if (item.name) nameToIndex.set(item.name, i);
      });

      const nodeTypes: NodeType[] = uniqueItems.map((item: any) =>
        labelToNodeType(item.label)
      );

      // Build edges — iterate over ALL retrievedNodes (including duplicates)
      // so that one node with multiple relationships creates multiple edges
      const allEdges: GraphEdge[] = [];
      const edgeKeySet = new Set<string>();
      let edgeIdx = 0;

      retrievedNodes.forEach((item: any) => {
        if (!item.related_to || !item.relationship) return;
        const rel = mapRelationship(item.relationship);

        // Find source node index from unique items via name lookup
        const displayName = getDisplayName(item);
        const sourceIdx =
          nameToIndex.get(displayName) ?? nameToIndex.get(item.name);
        if (sourceIdx === undefined) return;

        // Find target node index
        const targetIdx = nameToIndex.get(item.related_to);
        if (targetIdx === undefined) return;

        // Arrow always goes from current node to related_to node
        const source = `node-${sourceIdx}`;
        const target = `node-${targetIdx}`;

        // Avoid duplicate edges (include relationship type in key)
        const edgeKey = `${source}-${target}-${rel}`;
        const reverseKey = `${target}-${source}-${rel}`;
        if (!edgeKeySet.has(edgeKey) && !edgeKeySet.has(reverseKey)) {
          edgeKeySet.add(edgeKey);
          allEdges.push({
            id: `edge-${edgeIdx++}`,
            source,
            target,
            relationship: rel
          });
        }
      });

      // CONTAINS chain for layout
      const containsEdges = allEdges.filter(
        (e) => e.relationship === RelationshipType.CONTAINS
      );
      const containsChildren = new Map<number, number[]>();
      containsEdges.forEach((e) => {
        const src = parseInt(e.source.replace("node-", ""));
        const tgt = parseInt(e.target.replace("node-", ""));
        if (!containsChildren.has(src)) containsChildren.set(src, []);
        containsChildren.get(src)!.push(tgt);
      });

      const containsTargetSet = new Set(
        containsEdges.map((e) => parseInt(e.target.replace("node-", "")))
      );
      const containsSourceSet = new Set(
        containsEdges.map((e) => parseInt(e.source.replace("node-", "")))
      );
      let rootIdx = -1;
      containsSourceSet.forEach((s) => {
        if (!containsTargetSet.has(s)) rootIdx = s;
      });

      const containsChain: number[] = [];
      if (rootIdx !== -1) {
        let current: number | undefined = rootIdx;
        while (current !== undefined) {
          containsChain.push(current);
          const children: number[] = containsChildren.get(current) || [];
          current =
            children.length > 0
              ? children.sort(
                  (a, b) =>
                    typeHierarchy(nodeTypes[a]) - typeHierarchy(nodeTypes[b])
                )[0]
              : undefined;
        }
      }

      // Build parent-child map for HAS/OWNED_BY edges (parent in CONTAINS chain → children below)
      const hasChildren = new Map<number, number[]>();
      const nonContainsEdges = allEdges.filter(
        (e) => e.relationship !== RelationshipType.CONTAINS
      );

      // For each non-CONTAINS edge where source is in CONTAINS chain, group target as child
      nonContainsEdges.forEach((e) => {
        const srcIdx = parseInt(e.source.replace("node-", ""));
        const tgtIdx = parseInt(e.target.replace("node-", ""));
        if (containsChain.includes(srcIdx) && !containsChain.includes(tgtIdx)) {
          if (!hasChildren.has(srcIdx)) hasChildren.set(srcIdx, []);
          if (!hasChildren.get(srcIdx)!.includes(tgtIdx)) {
            hasChildren.get(srcIdx)!.push(tgtIdx);
          }
        }
      });

      // Collect all placed node indices
      const placedSet = new Set<number>(containsChain);
      hasChildren.forEach((children) =>
        children.forEach((c) => placedSet.add(c))
      );

      // Find remaining nodes not yet placed (e.g., Discipline connected via OWNED_BY from a requirement)
      const remainingIndices: number[] = [];
      uniqueItems.forEach((_: any, i: number) => {
        if (!placedSet.has(i)) remainingIndices.push(i);
      });

      // Try to attach remaining nodes to their connected parent in second row
      const thirdRowParent = new Map<number, number>();
      remainingIndices.forEach((ri) => {
        for (const e of nonContainsEdges) {
          const srcIdx = parseInt(e.source.replace("node-", ""));
          const tgtIdx = parseInt(e.target.replace("node-", ""));
          if (srcIdx === ri && placedSet.has(tgtIdx)) {
            thirdRowParent.set(ri, tgtIdx);
            break;
          }
          if (tgtIdx === ri && placedSet.has(srcIdx)) {
            thirdRowParent.set(ri, srcIdx);
            break;
          }
        }
      });

      // --- Position nodes in a tree layout ---
      const nodePositions: Record<number, { x: number; y: number }> = {};

      // Row 0: CONTAINS chain (all nodes, no limit)
      containsChain.forEach((idx, col) => {
        nodePositions[idx] = {
          x: START_X + col * COL_GAP,
          y: START_Y
        };
      });

      // Row 1: HAS children positioned below their parent
      const CHILD_GAP = 280;
      containsChain.forEach((parentIdx) => {
        const children = hasChildren.get(parentIdx) || [];
        if (children.length === 0) return;
        const parentX = nodePositions[parentIdx].x;
        const totalWidth = (children.length - 1) * CHILD_GAP;
        const startChildX = parentX - totalWidth / 2;
        children.forEach((childIdx, ci) => {
          nodePositions[childIdx] = {
            x: startChildX + ci * CHILD_GAP,
            y: START_Y + ROW_GAP
          };
        });
      });

      // Row 2: Remaining nodes below their connected node
      remainingIndices.forEach((ri, idx) => {
        const parentIdx = thirdRowParent.get(ri);
        if (parentIdx !== undefined && nodePositions[parentIdx]) {
          nodePositions[ri] = {
            x: nodePositions[parentIdx].x,
            y: nodePositions[parentIdx].y + ROW_GAP
          };
        } else {
          nodePositions[ri] = {
            x: START_X + idx * COL_GAP,
            y: START_Y + ROW_GAP * 2
          };
        }
      });

      // Resolve overlaps in row 1: shift nodes that are too close
      const row1Nodes = Array.from(placedSet).filter(
        (idx) =>
          !containsChain.includes(idx) &&
          nodePositions[idx]?.y === START_Y + ROW_GAP
      );
      row1Nodes.sort((a, b) => nodePositions[a].x - nodePositions[b].x);
      for (let i = 1; i < row1Nodes.length; i++) {
        const prev = nodePositions[row1Nodes[i - 1]];
        const curr = nodePositions[row1Nodes[i]];
        if (curr.x - prev.x < CHILD_GAP) {
          curr.x = prev.x + CHILD_GAP;
        }
      }

      const allNodes: GraphNode[] = uniqueItems.map((item: any, i: number) => {
        const pos = nodePositions[i] || { x: START_X, y: START_Y };
        return {
          id: `node-${i}`,
          label: item._displayName,
          type: labelToNodeType(item.label),
          x: pos.x,
          y: pos.y,
          propsCount: item.properties ? Object.keys(item.properties).length : 0
        };
      });

      const graphProperties: NodeProperties = {
        status: "Retrieved",
        criticality: "\u2014",
        lastUpdated: new Date().toISOString().split("T")[0]
      };

      const propsMap: Record<string, NodeProperties> = {};
      uniqueItems.forEach((item: any, i: number) => {
        if (item.properties && typeof item.properties === "object") {
          const nodeProps: NodeProperties = {};
          Object.entries(item.properties).forEach(([key, val]) => {
            if (
              val !== null &&
              val !== undefined &&
              String(val).trim() !== ""
            ) {
              nodeProps[key] = String(val);
            }
          });
          propsMap[`node-${i}`] = nodeProps;
        }
      });

      const graphConnectedEntities = uniqueItems.map(
        (item: any, i: number) => ({
          id: `ce-${i}`,
          name: item._displayName,
          category: (item.label || "").toUpperCase(),
          relationship: mapRelationship(item.relationship),
          type: labelToNodeType(item.label)
        })
      );

      return {
        nodes: allNodes,
        edges: allEdges,
        properties: graphProperties,
        connectedEntities: graphConnectedEntities,
        nodePropertiesMap: propsMap
      };
    }, [retrievedNodes]);

  const tabs = [
    { id: "overview", label: "Overview" },
    // { id: "specification", label: "Specification" },
    { id: "knowledge-graph", label: "Knowledge Graph" }
  ];

  return (
    <>
      {/* <HeaderNew showSearch={false} /> */}
      {loading || error ? (
        <SearchShimmer query={searchQuery} error={error} />
      ) : (
        <div className="search-details">
          <div className="search-details__content">
            {/* Results Header */}
            <div className="search-details__results-header">
              <div className="search-details__header-container">
                <div className="search-details__breadcrumb">
                  <span
                    className="search-details__breadcrumb-link"
                    onClick={() => navigate("/")}
                  >
                    Home
                  </span>
                  <span className="search-details__breadcrumb-separator">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M6 3L11 8L6 13"
                        stroke="#9897A6"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                  <span className="search-details__breadcrumb-current">
                    Search Results
                  </span>
                </div>
              </div>
              <div className="search-details__header-actions">
                <Button
                  className="search-details__config-btn"
                  onClick={() => {}}
                >
                  <Settings />
                  <span>Modification Configuration</span>
                </Button>
              </div>
            </div>

            <h1 className="search-details__title">
              {sr?.subject || searchQuery}
            </h1>

            <div className="search-details__tabs">
              <div className="search-details__tabs-container">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    className={`search-details__tab ${
                      activeTab === tab.id ? "search-details__tab--active" : ""
                    }`}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {activeTab === "overview" && (
              <>
                <div className="search-details__grid">
                  <div className="search-details__grid-row">
                    <div className="search-details__requirements-card">
                      <div className="search-details__requirements-header">
                        <div className="search-details__requirements-header-text">
                          <p className="search-details__card-subtitle">
                            {sr?.answer || kgAnswer || ""}
                          </p>
                        </div>
                      </div>

                      <div className="search-details__requirements-body">
                        <div className="search-details__requirement-section">
                          <span className="search-details__requirement-label">
                            CORE ANSWER
                          </span>
                          <ul className="search-details__requirement-list">
                            {sr?.core_answer && sr.core_answer.length > 0 ? (
                              sr.core_answer.map((item, i) => (
                                <li key={i}>{item}</li>
                              ))
                            ) : kgAnswer ? (
                              kgAnswer
                                .split("\n")
                                .filter((line: string) => line.trim())
                                .map((line: string, i: number) => (
                                  <li key={i}>{line.replace(/^-\s*/, "")}</li>
                                ))
                            ) : (
                              <li>No core answer available.</li>
                            )}
                          </ul>
                        </div>

                        <div className="search-details__requirement-section">
                          <span className="search-details__requirement-label">
                            CONFIDENCE SCORE
                          </span>
                          <div className="search-details__margins-container">
                            <div className="search-details__margin-item">
                              <span className="search-details__margin-label">
                                Score
                              </span>
                              <span className="search-details__margin-value">
                                {sr?.confidence_score != null
                                  ? Number(sr.confidence_score).toFixed(2)
                                  : kgData?.kg_used
                                  ? "KG Used"
                                  : "N/A"}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="search-details__context-card">
                      <div className="search-details__context-header">
                        <div className="search-details__context-icon">
                          <Info />
                        </div>
                        <span className="search-details__context-title">
                          SYSTEM CONTEXT
                        </span>
                      </div>

                      <div className="search-details__context-body">
                        <div className="search-details__context-item">
                          <span className="search-details__context-label">
                            PROJECT
                          </span>
                          <span className="search-details__context-value">
                            {assetNode?.properties?.facility_name ||
                              assetNode?.name ||
                              "—"}
                          </span>
                        </div>

                        <div className="search-details__context-item search-details__context-item--bordered">
                          <span className="search-details__context-label">
                            ASSET IDENTIFIER
                          </span>
                          <div className="search-details__context-asset">
                            <span className="search-details__context-dot"></span>
                            <span className="search-details__context-value">
                              {equipmentNode?.name ||
                                systemNode?.name ||
                                assetNode?.properties?.asset_id ||
                                "—"}
                            </span>
                          </div>
                        </div>

                        <div className="search-details__context-item search-details__context-item--bordered">
                          <span className="search-details__context-label">
                            LAST UPDATED
                          </span>
                          <span className="search-details__context-value">
                            {new Date().toLocaleDateString("en-US", {
                              year: "numeric",
                              month: "short",
                              day: "numeric"
                            })}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="search-details__grid-row">
                    <div className="search-details__traceability-card">
                      <div className="search-details__traceability-header">
                        <h3 className="search-details__traceability-title">
                          Source Traceability
                        </h3>
                        <button className="search-details__traceability-more">
                          <MoreHoriz />
                        </button>
                      </div>

                      <div className="search-details__traceability-table">
                        <div className="search-details__table-header">
                          <div className="search-details__table-cell search-details__table-cell--header">
                            CONTENT
                          </div>
                          <div className="search-details__table-cell search-details__table-cell--header search-details__table-cell--score">
                            SCORE
                          </div>
                          <div className="search-details__table-cell search-details__table-cell--header search-details__table-cell--action">
                            ACTION
                          </div>
                        </div>
                        {sr?.evidence_requirements?.map((item, index) => (
                          <div
                            key={index}
                            className="search-details__table-row"
                          >
                            <div className="search-details__table-cell search-details__table-cell--light">
                              {item.content?.split(/(?<=[.!?])\s/)[0] ||
                                item.content}
                            </div>
                            <div className="search-details__table-cell search-details__table-cell--score">
                              {item.score != null
                                ? Math.round(item.score * 100) / 100
                                : "N/A"}
                            </div>
                            <div className="search-details__table-cell search-details__table-cell--action">
                              View
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="search-details__knowledge-graph-card">
                      <div className="search-details__knowledge-graph-content">
                        <h3 className="search-details__knowledge-graph-title">
                          Knowledge Graph
                        </h3>
                        <p className="search-details__knowledge-graph-description">
                          {nodes.length > 0
                            ? `Visualizing ${nodes.length} entities and ${edges.length} relational links.`
                            : kgLoading
                            ? "Loading knowledge graph..."
                            : "No graph data available."}
                        </p>

                        <div className="search-details__knowledge-graph-visual">
                          <div className="search-details__node-border"></div>
                          <svg
                            className="search-details__node-connections"
                            viewBox="0 0 242 69"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <line
                              x1="108"
                              y1="16"
                              x2="175"
                              y2="44"
                              stroke="#000000"
                              strokeWidth="1"
                            />
                            <line
                              x1="108"
                              y1="16"
                              x2="68"
                              y2="22"
                              stroke="#000000"
                              strokeWidth="1"
                            />
                            <line
                              x1="175"
                              y1="44"
                              x2="118"
                              y2="44"
                              stroke="#000000"
                              strokeWidth="1"
                            />
                            <line
                              x1="110"
                              y1="44"
                              x2="68"
                              y2="22"
                              stroke="#000000"
                              strokeWidth="1"
                            />
                            <circle cx="108" cy="16" r="4" fill="#2E2E38" />
                            <circle cx="118" cy="44" r="7" fill="#0F69AE" />
                            <circle cx="175" cy="44" r="3.5" fill="#FFFFFF" />
                            <circle cx="68" cy="22" r="3.5" fill="#FFFFFF" />
                          </svg>
                        </div>

                        <Button
                          className="search-details__view-details-btn"
                          onClick={() => {
                            setActiveTab("knowledge-graph");
                            setTimeout(() => {
                              kgSectionRef.current?.scrollIntoView({
                                behavior: "smooth"
                              });
                            }, 100);
                          }}
                        >
                          View Details
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="search-details__supporting-docs">
                  <h3 className="search-details__supporting-docs-title">
                    Supporting Documents
                  </h3>
                  <div className="search-details__supporting-docs-grid">
                    {sr?.evidence_requirements?.map((doc, index) => (
                      <div key={index} className="search-details__doc-card">
                        <div className="search-details__doc-icon">
                          <svg
                            width="27"
                            height="27"
                            viewBox="0 0 27 27"
                            fill="none"
                          >
                            <path
                              d="M5 3C5 1.89543 5.89543 1 7 1H16L23 8V23C23 24.1046 22.1046 25 21 25H7C5.89543 25 5 24.1046 5 23V3Z"
                              fill="#94A3B8"
                            />
                            <path d="M16 1L23 8H16V1Z" fill="#CBD5E1" />
                          </svg>
                        </div>
                        <div className="search-details__doc-info">
                          <span className="search-details__doc-name">
                            Document ID: {doc.doc_id}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {activeTab === "knowledge-graph" && (
              <div
                ref={kgSectionRef}
                className="search-details__knowledge-graph-full"
              >
                {kgLoading ? (
                  <div className="search-details__knowledge-graph-no-data">
                    <p className="search-details__knowledge-graph-no-data-text">
                      Loading knowledge graph...
                    </p>
                  </div>
                ) : nodes.length > 0 ? (
                  <div className="search-details__kg-layout">
                    <div className="search-details__kg-graph-area">
                      <GraphCanvas
                        nodes={nodes}
                        edges={edges}
                        selectedNodeId={selectedNodeId}
                        onNodeSelect={setSelectedNodeId}
                      />
                    </div>
                    <div className="search-details__kg-sidebar">
                      <PropertiesPanel
                        properties={
                          selectedNodeId && nodePropertiesMap[selectedNodeId]
                            ? nodePropertiesMap[selectedNodeId]
                            : properties || {}
                        }
                        connectedEntities={connectedEntities}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="search-details__knowledge-graph-no-data">
                    <span className="search-details__knowledge-graph-no-data-emoji">
                      😔
                    </span>
                    <p className="search-details__knowledge-graph-no-data-text">
                      No data found
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default SearchDetails;
